import { openDB, type DBSchema } from "idb";

export type SyncQueueEntity =
  | "asset"
  | "photo"
  | "inventoryItem"
  | "serviceRequest"
  | "externalService"
  | "externalServiceItem"
  | "inspectionPhoto"
  | "damagePhoto";

interface ImobiLocalDb extends DBSchema {
  syncQueue: {
    key: string;
    value: {
      id: string;
      entity: SyncQueueEntity;
      action: "create" | "update" | "delete";
      payload: unknown;
      createdAt: string;
      attempts: number;
      lastError?: string;
    };
    indexes: { "by-created": string };
  };
  cachedEntities: {
    key: string;
    value: {
      id: string;
      collection: string;
      payload: unknown;
      updatedAt: string;
    };
    indexes: { "by-collection": string };
  };
}

export const SYNC_QUEUE_CHANGED_EVENT = "imobi:sync-queue-changed";

function notifySyncQueueChanged(): void {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(SYNC_QUEUE_CHANGED_EVENT));
  }
}

export const localDb = openDB<ImobiLocalDb>("imobi-local", 1, {
  upgrade(db) {
    const syncQueue = db.createObjectStore("syncQueue", { keyPath: "id" });
    syncQueue.createIndex("by-created", "createdAt");

    const cachedEntities = db.createObjectStore("cachedEntities", { keyPath: "id" });
    cachedEntities.createIndex("by-collection", "collection");
  }
});

export async function enqueueOfflineChange(value: ImobiLocalDb["syncQueue"]["value"]): Promise<void> {
  const db = await localDb;
  await db.put("syncQueue", value);
  notifySyncQueueChanged();
}

export async function listPendingOfflineChanges(): Promise<Array<ImobiLocalDb["syncQueue"]["value"]>> {
  const db = await localDb;
  return db.getAllFromIndex("syncQueue", "by-created");
}

export async function removeOfflineChange(id: string): Promise<void> {
  const db = await localDb;
  await db.delete("syncQueue", id);
  notifySyncQueueChanged();
}

export async function markOfflineChangeError(id: string, message: string): Promise<void> {
  const db = await localDb;
  const existing = await db.get("syncQueue", id);
  if (!existing) {
    return;
  }

  await db.put("syncQueue", { ...existing, attempts: existing.attempts + 1, lastError: message });
  notifySyncQueueChanged();
}
